import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';

import { CompanyService } from '../../services/company.service';
import { OfflineStateService } from '../../offline/services/offline-state.service';
import { PendingQueueService, PendingRequest } from '../../offline/services/pending-queue.service';
import { Company } from '../../models/company.model';

export interface SyncLogEntry {
  tableName: string;
  status: string;
  recordCount: number;
  durationMs: number | null;
  syncedAt: string;
  errorMessage: string | null;
}

export interface CompanyState {
  allCompanies: Company[];
  filteredSwitchCompanies: Company[];
  companySwitchSearch: string;
  userCompanies: { companyId: number; name: string; sigle?: string }[];
  showUserCompanySwitcher: boolean;
  selectedCompany: string | null;
  showSyncPanel: boolean;
  syncLogs: SyncLogEntry[];
  pendingRequests: PendingRequest[];
}

export class TopbarCompanyCollab {

  constructor(
    private companyService: CompanyService,
    private offlineState: OfflineStateService,
    private pendingQueue: PendingQueueService,
    private translate: TranslateService,
    private messageService: MessageService,
  ) {}

  loadSwitchCompanies(state: CompanyState): void {
    this.companyService.getAll().subscribe({
      next: (companies) => {
        console.log('[Topbar] loadSwitchCompanies → got', companies.length, 'companies');
        state.allCompanies = companies;
        state.filteredSwitchCompanies = companies;
      },
      error: (err) => {
        console.error('[Topbar] loadSwitchCompanies FAILED:', err);
      }
    });
  }

  loadUserCompanies(state: CompanyState): void {
    this.companyService.getMyCompanies().subscribe({
      next: (memberships) => {
        if (memberships.length > 1) {
          const currentId = Number(localStorage.getItem('selectedCompanyId'));
          state.userCompanies = memberships
            .filter(m => m.companyId !== currentId)
            .map(m => ({
              companyId: m.companyId,
              name: (m as any).name || this.translate.instant('CHOOSE_COMPANY.FALLBACK_NAME', { id: m.companyId }),
              sigle: (m as any).sigle,
            }));
          state.showUserCompanySwitcher = state.userCompanies.length > 0;
        }
      },
      error: () => { /* silently fail */ }
    });
  }

  selectUserCompany(company: { companyId: number; name: string }, state: CompanyState, closeAll: () => void): void {
    const applique = this.companyService.appliquerEntrepriseChoisie({
      id: company.companyId,
      name: company.name,
    });
    if (!applique) {
      this.avertirBasculeImpossible(closeAll);
      return;
    }
    state.selectedCompany = company.name;
    closeAll();
    window.location.reload();
  }

  selectSwitchCompany(company: Company, state: CompanyState, closeAll: () => void): void {
    if (!this.companyService.appliquerEntrepriseChoisie(company)) {
      this.avertirBasculeImpossible(closeAll);
      return;
    }
    state.selectedCompany = company.name;
    closeAll();
    window.location.reload();
  }

  filterSwitchCompanies(state: CompanyState): void {
    if (!state.companySwitchSearch.trim()) {
      state.filteredSwitchCompanies = state.allCompanies;
      return;
    }
    const term = state.companySwitchSearch.toLowerCase();
    state.filteredSwitchCompanies = state.allCompanies.filter(c =>
      c.name.toLowerCase().includes(term) ||
      c.sigle?.toLowerCase().includes(term)
    );
  }

  toggleSyncPanel(state: CompanyState, closeAll: () => void): void {
    state.showSyncPanel = !state.showSyncPanel;
    if (state.showSyncPanel) {
      closeAll();
      this.loadSyncData(state);
    }
  }

  closeSyncPanel(state: CompanyState): void {
    state.showSyncPanel = false;
  }

  async loadSyncData(state: CompanyState): Promise<void> {
    state.pendingRequests = this.pendingQueue.getPending();

    const db = this.offlineState.getDb();
    if (!db) {
      state.syncLogs = [];
      return;
    }
    try {
      const docs = await db.sync_log.find({
        selector: { direction: 'PULL' },
        sort: [{ syncedAt: 'desc' }],
      }).exec();

      const latest = new Map<string, SyncLogEntry>();
      for (const doc of docs) {
        const json = doc.toJSON() as SyncLogEntry;
        if (!latest.has(json.tableName)) {
          latest.set(json.tableName, json);
        }
      }
      state.syncLogs = Array.from(latest.values());
    } catch {
      state.syncLogs = [];
    }
  }

  private avertirBasculeImpossible(closeAll: () => void): void {
    closeAll();
    this.messageService.add({
      severity: 'warn',
      summary: this.translate.instant('COMPANY.SWITCH_OFFLINE'),
      detail: this.translate.instant('COMPANY.SWITCH_OFFLINE_DETAIL'),
      life: 6000,
    });
  }
}
